/**
 * PublicBookingPage — unauthenticated co-branded scheduling page at
 * /book/:handle/:slug (handle = owner's booking handle, slug = page slug,
 * unique per handle).
 *
 * Renders the Origin-owned co-brand header (LO + optional partner headshots,
 * NMLS compliance line, headline/intro, accent color) from crm_booking_pages,
 * then mounts the Cal.diy inline embed for the row's cal_event_url.
 *
 * Prefill: ?name=&email=&phone= query params pass through to the embed config
 * (used by "Send Booking Link" from the contact drawer and campaign merge
 * fields). Borrowers reach this page by link only — there is no index.
 *
 * LegacyBookingRedirect handles pre-namespacing /book/:slug links (old SMS
 * and emails) by resolving the slug and 301-style replacing to the new URL.
 *
 * Link permanence: shared links must outlive edits (signatures, social bios).
 * A stale slug under a valid handle falls back to that owner's default active
 * page; /book/{handle} alone resolves the default via LegacyBookingRedirect.
 * Server-side, calSaveBookingPage forbids deactivating the default page and
 * calDeleteBookingPage promotes a replacement default, so the fallback target
 * always exists while the LO has any active page.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Avatar,
  AvatarGroup,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { supabase } from '../../utils/supabase';
import { CAL_BASE_URL, calLinkFromEventUrl } from '../../constants/calScheduling';

const EMBED_NAMESPACE = 'fhlBookingPage';

/** Official Cal embed loader (vanilla snippet, pointed at our instance). */
function loadCalEmbed(calOrigin) {
  /* eslint-disable */
  (function (C, A, L) {
    let p = function (a, ar) { a.q.push(ar); };
    let d = C.document;
    C.Cal = C.Cal || function () {
      let cal = C.Cal;
      let ar = arguments;
      if (!cal.loaded) {
        cal.ns = {};
        cal.q = cal.q || [];
        d.head.appendChild(d.createElement('script')).src = A;
        cal.loaded = true;
      }
      if (ar[0] === L) {
        const api = function () { p(api, arguments); };
        const namespace = ar[1];
        api.q = api.q || [];
        if (typeof namespace === 'string') {
          cal.ns[namespace] = cal.ns[namespace] || api;
          p(cal.ns[namespace], ar);
          p(cal, ['initNamespace', namespace]);
        } else p(cal, ar);
        return;
      }
      p(cal, ar);
    };
  })(window, `${calOrigin}/embed/embed.js`, 'init');
  /* eslint-enable */
}

/** Shared "page unavailable" state for both the live page and legacy redirects. */
function BookingPageNotFound() {
  return (
    <Center minH="100vh" bg="gray.50" flexDir="column" px={4}>
      <Heading size="md" mb={2} color="gray.700">This booking page isn't available</Heading>
      <Text color="gray.500" textAlign="center">
        The link may be incorrect or the page has been turned off. Please reach out to your loan officer directly.
      </Text>
    </Center>
  );
}

/**
 * Legacy /book/:slug resolver. Looks the slug up globally (pilot-era links),
 * then falls back to treating the segment as a handle (old /book/{handle}
 * default links), and replaces the URL with the namespaced form.
 */
export function LegacyBookingRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let { data } = await supabase
        .from('crm_booking_pages')
        .select('owner_handle, slug')
        .eq('slug', slug)
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (!data) {
        ({ data } = await supabase
          .from('crm_booking_pages')
          .select('owner_handle, slug')
          .eq('owner_handle', slug)
          .eq('is_default', true)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle());
      }
      if (cancelled) return;
      if (data) {
        history.replace(`/book/${data.owner_handle}/${data.slug}${location.search}`);
      } else {
        setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, location.search, history]);

  if (notFound) return <BookingPageNotFound />;
  return (
    <Center minH="100vh" bg="gray.50">
      <Spinner size="xl" color="blue.500" thickness="4px" />
    </Center>
  );
}

export default function PublicBookingPage() {
  const { handle, slug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound

  const prefill = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const config = {};
    if (params.get('name')) config.name = params.get('name');
    if (params.get('email')) config.email = params.get('email');
    if (params.get('phone')) config.phone = params.get('phone');
    return config;
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('crm_booking_pages')
        .select('*')
        .eq('owner_handle', handle)
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data) {
        setPage(data);
        setStatus('ready');
        document.title = data.title || 'Book a Call';
        return;
      }
      // Self-healing for long-lived shared links (email signatures, social
      // posts): if the slug is stale (renamed/deactivated/deleted page) but
      // the handle is real, land the visitor on that LO's default page
      // instead of a dead end. The URL is left as-is on purpose — it keeps
      // rendering wherever the default points, even if the default changes.
      const { data: fallback } = await supabase
        .from('crm_booking_pages')
        .select('*')
        .eq('owner_handle', handle)
        .eq('is_default', true)
        .eq('is_active', true)
        .maybeSingle();
      if (cancelled) return;
      if (fallback) {
        setPage(fallback);
        setStatus('ready');
        document.title = fallback.title || 'Book a Call';
      } else {
        setStatus('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [handle, slug]);

  // Mount the Cal.diy inline embed once the page row is loaded
  useEffect(() => {
    if (status !== 'ready' || !page) return undefined;
    const calLink = calLinkFromEventUrl(page.cal_event_url);
    if (!calLink) return undefined;

    loadCalEmbed(CAL_BASE_URL);
    window.Cal('init', EMBED_NAMESPACE, { origin: CAL_BASE_URL });
    window.Cal.ns[EMBED_NAMESPACE]('inline', {
      elementOrSelector: '#fhl-cal-embed',
      calLink,
      layout: 'month_view',
      config: { ...prefill, theme: 'light' },
    });
    window.Cal.ns[EMBED_NAMESPACE]('ui', {
      hideEventTypeDetails: false,
      cssVarsPerTheme: {
        light: { 'cal-brand': page.accent_color || '#1A365D' },
      },
    });

    return () => {
      const el = document.getElementById('fhl-cal-embed');
      if (el) el.innerHTML = '';
    };
  }, [status, page, prefill]);

  if (status === 'loading') {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    );
  }

  if (status === 'notfound') {
    return <BookingPageNotFound />;
  }

  const accent = page.accent_color || '#1A365D';
  const hasPartner = !!(page.partner_name || page.partner_headshot_url);

  return (
    <Box minH="100vh" bg="gray.50">
      <Box h="8px" style={{ backgroundColor: accent }} />
      <Container maxW="900px" py={{ base: 6, md: 10 }} px={{ base: 3, md: 6 }}>
        <VStack spacing={5} mb={8} textAlign="center">
          <AvatarGroup size="xl" max={2} spacing={hasPartner ? '-0.9rem' : 0}>
            <Avatar
              src={page.lo_headshot_url || undefined}
              name={page.lo_name || 'Loan Officer'}
              borderWidth="3px"
              borderColor="white"
              shadow="md"
            />
            {hasPartner && (
              <Avatar
                src={page.partner_headshot_url || undefined}
                name={page.partner_name || 'Partner'}
                borderWidth="3px"
                borderColor="white"
                shadow="md"
              />
            )}
          </AvatarGroup>

          <Box>
            <Heading size="lg" color="gray.800">
              {page.headline || page.title || 'Schedule time with us'}
            </Heading>
            {page.intro_text && (
              <Text mt={3} color="gray.600" maxW="640px" mx="auto">
                {page.intro_text}
              </Text>
            )}
          </Box>

          <HStack
            spacing={{ base: 4, md: 10 }}
            justify="center"
            align="start"
            flexWrap="wrap"
          >
            <VStack spacing={0}>
              <Text fontWeight="bold" color="gray.800">{page.lo_name || 'Loan Officer'}</Text>
              <Text fontSize="sm" color="gray.500">{page.lo_title || 'Loan Officer'}</Text>
              <Text fontSize="sm" color="gray.500">Future Home Loans</Text>
            </VStack>
            {hasPartner && (
              <VStack spacing={0}>
                <Text fontWeight="bold" color="gray.800">{page.partner_name}</Text>
                {page.partner_title && <Text fontSize="sm" color="gray.500">{page.partner_title}</Text>}
                {page.partner_company && <Text fontSize="sm" color="gray.500">{page.partner_company}</Text>}
              </VStack>
            )}
          </HStack>
        </VStack>

        <Box
          id="fhl-cal-embed"
          bg="white"
          borderRadius="xl"
          shadow="sm"
          borderWidth="1px"
          borderColor="gray.200"
          overflow="hidden"
          minH="620px"
          width="100%"
        />

        <VStack spacing={1} mt={8} pb={6}>
          {page.lo_nmls && (
            <Text fontSize="xs" color="gray.400">
              {page.lo_name || 'Loan Officer'} — NMLS #{page.lo_nmls} — Future Home Loans
            </Text>
          )}
          <Text fontSize="xs" color="gray.400">
            Equal Housing Lender. All times shown in your local time zone.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}
